import _ from 'lodash'
import globals from '@/plugins/globals'

export const BrokenLinkStatus = {
  Reported: 1,
  NoLinkBuilt: 2, /** No calibration links exist for the vehicle/system at all. */
  UnderReview: 3,
  Resolved: 4,
  NoLinksFound: 5 /** A link was reported broken and the ALLDATA search turned up no replacement. */
}

/** Every status, in the order the admin grid and its filter should list them. */
export const brokenLinkStatuses = [
  { value: BrokenLinkStatus.Reported, name: 'User Reported', class: 'text-danger font-weight-bold' },
  { value: BrokenLinkStatus.NoLinkBuilt, name: 'No Link Built', class: 'text-warning font-weight-bold' },
  { value: BrokenLinkStatus.UnderReview, name: 'Under Review', class: 'text-primary font-weight-bold' },
  { value: BrokenLinkStatus.Resolved, name: 'Resolved', class: 'text-success font-weight-bold' },
  { value: BrokenLinkStatus.NoLinksFound, name: 'No Replacement Found', class: 'text-warning font-weight-bold' }
]

export const DEFAULT_GROUP_CATEGORY_NAME = 'Calibration / Inspection Links'

/** AdasLinks.LookupType, as the matrix editors name it. */
export const ADASLINKS_TYPES = [
  { value: 1, name: 'System' },
  { value: 2, name: 'Location' },
  { value: 3, name: 'Static' },
  { value: 4, name: 'Dynamic' }
]

const UNKNOWN_STATUS = { value: null, name: '', class: '' }

const BREADCRUMB_SEPARATOR = '>>'
const BREADCRUMB_ICON = '<i class="fas fa-angle-double-right"></i>'
const GENERAL_LOOKUP_TYPE = 2
// A search term is whatever follows the first marker found, in priority order.
// "Calibration Procedure" can be numbered ("Calibration Procedure 2 - Front Radar"),
// so the number and the separating dash are consumed along with the marker.
const SEARCH_TERM_MARKERS = [/ALLDATA Service Information\s*-\s*/, /Calibration Procedure\s*\d*\s*-?\s*/]

// ALLDATA's search reads these as operators, so they get flattened to spaces in a
// search term. Add to this list as more offenders turn up.
const SEARCH_TERM_SYMBOLS = ['[', ']', '/', '\\']

// Derived from the list above so that stays the only place to edit. Inside a character
// class only ] \ ^ - need escaping.
const SEARCH_TERM_SYMBOL_PATTERN = new RegExp(
  `[${SEARCH_TERM_SYMBOLS.map((symbol) => symbol.replace(/[\]\\^-]/g, '\\$&')).join('')}]+`,
  'g'
)

export function getBrokenLinkStatus(value) {
  return brokenLinkStatuses.find((status) => status.value === value) || UNKNOWN_STATUS
}

/**
 * Clicks recorded against one reported link. The count lives in the link's stored ALLDATA
 * suggestions, which the api passes through as a raw JSON string.
 */
export function clicksFor(link) {
  if (!link || !link.suggestionsData || !globals.isJson(link.suggestionsData)) {
    return 0
  }
  let suggestions = JSON.parse(link.suggestionsData)
  if (!Array.isArray(suggestions)) {
    return 0
  }
  return suggestions.reduce((total, x) => total + ((x && x.clickCount) || 0), 0)
}

/** The no-results placeholder ADAS Map stores in place of suggestions - it only carries the hit count. */
export function isNoLinkBuiltOrFound(suggestion) {
  return !!suggestion && (suggestion.isNoLinkBuilt === true || suggestion.isNoLinksFound === true)
}

function sanitizeSearchTerm(value) {
  return (value || '').replace(SEARCH_TERM_SYMBOL_PATTERN, ' ').replace(/\s+/g, ' ').trim()
}

export function getUrlMaps() {
  return {
    articleMap: import.meta.env.VITE_ALLDATA_URL_MAP || (window.config ? window.config.alldataUrlMap : '') || '',
    filterMap: import.meta.env.VITE_ALLDATA_URL_FILTER_MAP || (window.config ? window.config.alldataUrlFilterMap : '') || ''
  }
}

function toPayload(value) {
  if (value === undefined || value === null || value === '') {
    return null
  }
  if (typeof value !== 'string') {
    return value
  }
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

/** Walk a chain of property names, giving up quietly on the first gap. */
function dig(value, path) {
  let current = value
  for (let idx = 0; idx < path.length; idx++) {
    if (current === undefined || current === null) {
      return null
    }
    current = current[path[idx]]
  }
  return current === undefined ? null : current
}

function toArray(value) {
  return Array.isArray(value) ? value : []
}

/**
 * Grouped results from a link lookup: `_embedded.data.GEN3NA._embedded.data.results`,
 * each entry being a groupCategory plus its own `results` hits.
 */
export function getGroupedResults(value) {
  const payload = toPayload(value)
  return toArray(dig(payload, ['_embedded', 'data', 'GEN3NA', '_embedded', 'data', 'results']))
}

/** Hits from a keyword search: `_embedded.data.results`. */
export function getSearchResults(value) {
  const payload = toPayload(value)
  return toArray(dig(payload, ['_embedded', 'data', 'results']))
}

/**
 * The ">>" separated `display` breadcrumb, minus its last segment (which is the
 * link text itself), rendered with chevrons between the remaining crumbs.
 */
export function buildBreadcrumbTitle(display) {
  const contents = String(display || '').split(BREADCRUMB_SEPARATOR)
  let title = ''
  for (let idx = 0; idx < contents.length - 1; idx++) {
    title = title + contents[idx]
    if (idx < contents.length - 2) {
      title = title + BREADCRUMB_ICON
    }
  }
  return title
}

export function readArticleHref(link) {
  const data = {
    carid: '',
    component: '',
    itype: '',
    article: ''
  }
  let url = null
  try {
    url = new URL(link)
  } catch {
    return null
  }
  let last = ''
  url.pathname.split('/').forEach((element) => {
    if (last === 'carids') {
      data.carid = element
    } else if (last === 'components') {
      data.component = element
    } else if (last === 'itypes') {
      data.itype = element
    } else if (last === 'nonstandards') {
      data.article = element
    }
    last = element
  })
  return data.carid === '' ? null : data
}

export function isAlldataApiHref(url) {
  return readArticleHref(url) !== null
}

export function mapArticleUrl(link, maps) {
  const urlMaps = maps || getUrlMaps()
  const data = readArticleHref(link)
  if (data === null) {
    return link
  }
  const hasArticle = data.itype !== '' && data.article !== ''
  let newUrl = hasArticle ? urlMaps.articleMap : urlMaps.filterMap
  newUrl = newUrl.replace('{{carid}}', data.carid)
  newUrl = newUrl.replace('{{component}}', data.component)
  if (hasArticle) {
    newUrl = newUrl.replace('{{itype}}', data.itype)
    newUrl = newUrl.replace('{{article}}', data.article)
  }
  return newUrl
}

/**
 * Turn one ALLDATA hit into a displayable link. Returns null for a hit we can't
 * render (no breadcrumb, or no self href to point at).
 */
export function buildLink(hit, category, maps) {
  if (!hit || !hit.display) {
    return null
  }
  const href = dig(hit, ['_links', 'self', 'href'])
  if (!href) {
    return null
  }
  const contents = String(hit.display).split(BREADCRUMB_SEPARATOR)
  return {
    title: buildBreadcrumbTitle(hit.display),
    article: contents[contents.length - 1],
    name: hit.display,
    url: hit.isOpus === undefined ? mapArticleUrl(href, maps) : href,
    // the stored self href, which is what a save matches the row on
    href: href,
    category: category
  }
}

export function buildManualLink(display, url, category) {
  const contents = String(display || '').split(BREADCRUMB_SEPARATOR)
  return {
    title: buildBreadcrumbTitle(display),
    article: contents[contents.length - 1],
    name: String(display || ''),
    url: String(url || ''),
    // nothing to translate, so the stored href is the url as typed
    href: String(url || ''),
    category: category === undefined ? null : category,
    isNew: true
  }
}

/** The stored self href of one payload entry, trimmed, or '' if it has none. */
function hrefOf(result) {
  return String(dig(result, ['_links', 'self', 'href']) || '').trim()
}

function trimmed(value) {
  return String(value === undefined || value === null ? '' : value).trim()
}

/** Every groupCategory in the payload, in stored order. */
function resultsOf(payload) {
  const results = dig(payload, ['_embedded', 'data', 'GEN3NA', '_embedded', 'data', 'results'])
  return Array.isArray(results) ? results : null
}

export function findTargetGroupIndex(results, options, change) {
  const groups = toArray(results)
  const opts = options || {}
  const categoryId = change && change.categoryId !== undefined && change.categoryId !== null ? change.categoryId : null
  if (categoryId !== null) {
    // eslint-disable-next-line eqeqeq
    const byChange = groups.findIndex((group) => dig(group, ['groupCategory', 'id']) == categoryId)
    if (byChange !== -1) {
      return byChange
    }
  }
  if (opts.groupCategoryId !== undefined && opts.groupCategoryId !== null) {
    // eslint-disable-next-line eqeqeq
    const byRecord = groups.findIndex((group) => dig(group, ['groupCategory', 'id']) == opts.groupCategoryId)
    if (byRecord !== -1) {
      return byRecord
    }
  }
  const name = String(opts.groupCategoryName || DEFAULT_GROUP_CATEGORY_NAME).toLowerCase()
  const byName = groups.findIndex((group) => String(dig(group, ['groupCategory', 'name']) || '').toLowerCase() === name)
  if (byName !== -1) {
    return byName
  }

  return groups.length === 1 ? 0 : -1
}

/** The envelope a record that holds no links at all starts from. */
function seedPayload() {
  return {
    _embedded: {
      data: {
        GEN3NA: {
          _embedded: {
            data: {
              dataSource: { name: 'OPUS IVS' },
              results: []
            }
          }
        }
      }
    }
  }
}

function seedGroup(options) {
  const opts = options || {}
  return {
    groupCategory: {
      id: opts.groupCategoryId === undefined ? null : opts.groupCategoryId,
      name: opts.groupCategoryName || DEFAULT_GROUP_CATEGORY_NAME
    },
    results: []
  }
}

export function applyLinkChangesDetailed(rawLinks, changes, options) {
  const opts = options || {}
  const outcome = {
    json: null,
    payload: null,
    ok: true,
    reason: null,
    added: 0,
    removed: 0,
    updated: 0,
    groupsRemoved: 0,
    skipped: []
  }
  const pending = toArray(changes)
  if (!pending.length) {
    outcome.reason = 'no-changes'
    return outcome
  }

  const isEmptySource = rawLinks === undefined || rawLinks === null || rawLinks === ''
  const hasNewLinks = pending.some((change) => change && change.isNew && trimmed(change.newUrl) && trimmed(change.documentTitle))

  let payload = null
  if (isEmptySource) {
    if (!hasNewLinks) {
      outcome.reason = 'no-effective-changes'
      return outcome
    }
    payload = seedPayload()
  } else {
    if (typeof rawLinks === 'string') {
      try {
        payload = JSON.parse(rawLinks)
      } catch {
        outcome.ok = false
        outcome.reason = 'unreadable-payload'
        return outcome
      }
    } else {
      payload = rawLinks
    }
    // toPayload hands back the caller's own object, so clone before touching anything
    payload = JSON.parse(JSON.stringify(payload))
    if (resultsOf(payload) === null) {
      outcome.ok = false
      outcome.reason = 'unreadable-payload'
      return outcome
    }
  }

  const groups = resultsOf(payload)
  // a group is only dropped when it ends up empty AND this run is what emptied it
  const touched = []
  const seededGroups = []

  const findAll = (href) => {
    const found = []
    groups.forEach((group, groupIdx) => {
      toArray(group.results).forEach((result, resultIdx) => {
        if (hrefOf(result) === href) {
          found.push({ groupIdx, resultIdx, result })
        }
      })
    })
    return found
  }
  const hasHref = (href) => findAll(href).length > 0

  pending.forEach((change) => {
    if (!change) {
      return
    }
    const documentTitle = trimmed(change.documentTitle)
    const newUrl = trimmed(change.newUrl)
    const originalUrl = trimmed(change.originalUrl)

    if (change.isNew) {
      if (!newUrl) {
        outcome.skipped.push({ change, reason: 'no-url' })
        return
      }
      if (!documentTitle) {
        // buildLink drops an entry with no display, so it would be stored and invisible
        outcome.skipped.push({ change, reason: 'no-title' })
        return
      }
      if (hasHref(newUrl)) {
        outcome.skipped.push({ change, reason: 'duplicate-url' })
        return
      }
      let groupIdx = findTargetGroupIndex(groups, opts, change)
      if (groupIdx === -1) {
        groups.push(seedGroup(opts))
        groupIdx = groups.length - 1
        seededGroups.push(groupIdx)
      }
      if (!Array.isArray(groups[groupIdx].results)) {
        groups[groupIdx].results = []
      }
      groups[groupIdx].results.push({
        display: documentTitle,
        isOpus: true,
        isNew: true,
        _links: { self: { href: newUrl } }
      })
      outcome.added++
      return
    }

    if (!originalUrl) {
      outcome.skipped.push({ change, reason: 'unmatched' })
      return
    }
    const matches = findAll(originalUrl)
    if (!matches.length) {
      outcome.skipped.push({ change, reason: 'unmatched' })
      return
    }

    if (change.isDeleted) {
      // every copy goes, or the one the admin removed comes back on the next read
      matches
        .slice()
        .sort((a, b) => b.groupIdx - a.groupIdx || b.resultIdx - a.resultIdx)
        .forEach((match) => {
          groups[match.groupIdx].results.splice(match.resultIdx, 1)
          if (touched.indexOf(match.groupIdx) === -1) {
            touched.push(match.groupIdx)
          }
          outcome.removed++
        })
      return
    }

    matches.slice(1).forEach(() => outcome.skipped.push({ change, reason: 'duplicate-match' }))
    const target = matches[0].result
    let changed = false
    if (documentTitle && documentTitle !== trimmed(target.display)) {
      target.display = documentTitle
      changed = true
    }
    if (newUrl && newUrl !== hrefOf(target)) {
      target._links = target._links || {}
      target._links.self = target._links.self || {}
      target._links.self.href = newUrl
      const isOpusUrl = change.isOpusUrl === undefined ? !isAlldataApiHref(newUrl) : !!change.isOpusUrl
      if (isOpusUrl) {
        target.isOpus = true
      }
      changed = true
    }
    if (changed) {
      outcome.updated++
    }
  })

  // a group we emptied goes; one that arrived empty and was never touched stays
  for (let groupIdx = groups.length - 1; groupIdx >= 0; groupIdx--) {
    const wasTouched = touched.indexOf(groupIdx) !== -1 || seededGroups.indexOf(groupIdx) !== -1
    if (wasTouched && toArray(groups[groupIdx].results).length === 0) {
      groups.splice(groupIdx, 1)
      outcome.groupsRemoved++
    }
  }

  if (!outcome.added && !outcome.removed && !outcome.updated) {
    outcome.reason = 'no-effective-changes'
    return outcome
  }

  if (opts.stampDataSource) {
    const data = dig(payload, ['_embedded', 'data', 'GEN3NA', '_embedded', 'data'])
    if (data) {
      data.dataSource = { name: String(opts.stampDataSource) }
    }
  }
  if (opts.dropCar !== false) {
    delete payload.car
  }

  outcome.payload = payload
  outcome.json = JSON.stringify(payload)
  return outcome
}

export function applyLinkChanges(rawLinks, changes, options) {
  return applyLinkChangesDetailed(rawLinks, changes, options).json
}

/** One grouped result (a groupCategory plus its hits) becomes one accordion group. */
function buildGroup(result, maps) {
  const links = []
  toArray(result.results).forEach((hit) => {
    const link = buildLink(hit, dig(result, ['groupCategory', 'id']), maps)
    if (link) {
      links.push(link)
    }
  })
  return {
    group: dig(result, ['groupCategory', 'name']) || '',
    links: links
  }
}

/** The flat "General" lookup list -- a single group of plain keyword/url rows. */
export function buildGeneralGroups(links) {
  if (!Array.isArray(links)) {
    return []
  }
  const generalLinks = links
    .filter((x) => x.lookupType === GENERAL_LOOKUP_TYPE)
    .map((link) => ({
      article: link.keywords,
      url: link.url,
      category: link.lookupType
    }))
  return [
    {
      group: 'General',
      links: generalLinks
    }
  ]
}

export function buildAlldataGroups(links, maps) {
  const urlMaps = maps || getUrlMaps()
  const groups = []
  const payloads = Array.isArray(links) ? links.map((element) => (element ? element.links : null)) : [links]
  payloads.forEach((payload) => {
    getGroupedResults(payload).forEach((result) => {
      groups.push(buildGroup(result, urlMaps))
    })
  })
  return groups
}

export function buildLinkGroups(links, options) {
  const opts = options || {}
  if (links === undefined || links === null) {
    return []
  }
  if (opts.source === 'General') {
    return buildGeneralGroups(links)
  }
  return buildAlldataGroups(links, opts.maps)
}

/**
 * Replacement links for a broken one, most relevant first and de-duplicated by URL.
 */
export function buildAlternateLinks(results, maps) {
  const urlMaps = maps || getUrlMaps()
  const suggestions = []
  const hits = getSearchResults(results)
    .slice()
    .sort((a, b) => relevanceOf(b) - relevanceOf(a))
  hits.forEach((hit) => {
    const link = buildLink(hit, dig(hit, ['itypeCategory', 'id']), urlMaps)
    if (!link || suggestions.some((suggestion) => suggestion.url === link.url)) {
      return
    }
    suggestions.push({
      ...link,
      relevance: hit.relevance && !isNaN(hit.relevance) ? parseInt(hit.relevance) : null,
      group: dig(hit, ['itypeCategory', 'name']) || ''
    })
  })
  return suggestions
}

function relevanceOf(hit) {
  return hit && typeof hit.relevance === 'number' ? hit.relevance : -Infinity
}

/**
 * Pull the carid, itype and a search term out of a link the user reported as
 * broken, so we can go looking for a replacement article.
 */
export function parseLink(link, isAdasLink = false) {
  const url = (link && link.url) || ''
  const carIdMatch = /\/article\/(\d+)\//.exec(url)
  const itypeMatch = /\/itype\/(\d+)\//.exec(url)

  let searchTerm = (link && link.article) || ''
  if (isAdasLink) {
    searchTerm = (link && link.display) || ''
  }
  for (const marker of SEARCH_TERM_MARKERS) {
    const markerMatch = marker.exec(searchTerm)
    if (markerMatch) {
      searchTerm = searchTerm.substring(markerMatch.index + markerMatch[0].length)
      break
    }
  }
  // Only after the markers are matched -- they rely on the literal dashes.
  searchTerm = sanitizeSearchTerm(searchTerm)

  return {
    carid: carIdMatch ? carIdMatch[1] : null,
    searchTerm: searchTerm,
    itype: itypeMatch ? itypeMatch[1] : null
  }
}

/* ---------- LaunchPad master link lookup ---------- */

export function normalize(value) {
  return String(value === null || value === undefined ? '' : value)
    .trim()
    .toLowerCase()
}

export function isMakeMatrix(model) {
  return !normalize(model && model.matrixModel)
}

/** The model the report carries, in the matrix's spelling where it has one. */
function reportedModel(model) {
  return String((model && (model.matrixModel || model.vehicleModel)) || '').trim()
}

function modelMatches(model, masterModel) {
  const reported = normalize(reportedModel(model))
  const master = normalize(masterModel)
  if (!reported || !master) {
    return true
  }
  return master.indexOf(reported) !== -1 || reported.indexOf(master) !== -1
}

function yearInRange(model, row) {
  const year = parseInt(row.year)
  if (!year) {
    return true
  }

  // Current matrix
  const start = row.rangeStart ? parseInt(row.rangeStart) : 0
  const end = row.rangeEnd ? parseInt(row.rangeEnd) : 9999
  // From reported matrix
  const yearStart = model.yearStart ? parseInt(model.yearStart) : 0
  const yearEnd = model.yearEnd ? parseInt(model.yearEnd) : 9999
  return year >= yearStart && year <= yearEnd && start === yearStart && end === yearEnd
}

function partMatches(reported, value) {
  const left = normalize(reported)
  return !left || left === normalize(value)
}

/** The reported matrix record among the make's -- its category, system and location. */
function matchesMatrixRecord(model, record) {
  return (
    partMatches(model.adasCategory, record.adasChecklistMatrixName) &&
    partMatches(model.system, record.system) &&
    partMatches(model.location, record.location)
  )
}

/** A matrix record's links, in the shape of the links listing rows the panel reads. */
function toLinkRows(record, links, makeName) {
  return (links || []).map((link) => ({
    adasLinks: link.adaslinks,
    year: link.year,
    vehicleMake: link.vehicleMake,
    make: makeName,
    model: link.model,
    lookupType: link.lookupType,
    adasChecklistMatrix: record.adasChecklistMatrix,
    adasChecklistMatrixVehicle: record.adasChecklistMatrixVehicle,
    category: record.adasChecklistMatrixName,
    system: record.system,
    location: record.location,
    rangeStart: record.yearStart,
    rangeEnd: record.yearEnd
  }))
}

/** Master ids are per-database, so the make has to be resolved by name. */
async function resolveMake(model, services) {
  const name = normalize(model ? model.vehicleMakeName : '')
  if (!name) {
    return null
  }
  const makes = (await services.getVehicleMakes()) || []
  return makes.find((x) => normalize(x.name) === name) || null
}

/**
 * The matrix record a new link record is added to, for a vehicle that holds none yet.
 * The reported range decides it; a lone record is taken as is.
 */
function toGroupRecord(model, records, make) {
  const yearStart = model.yearStart ? parseInt(model.yearStart) : 0
  const yearEnd = model.yearEnd ? parseInt(model.yearEnd) : 9999
  const record =
    records.find((x) => (parseInt(x.yearStart) || 0) === yearStart && (parseInt(x.yearEnd) || 9999) === yearEnd) ||
    (records.length === 1 ? records[0] : null)
  if (!record) {
    return null
  }
  return {
    adasChecklistMatrix: record.adasChecklistMatrix,
    adasChecklistMatrixVehicle: record.adasChecklistMatrixVehicle,
    adasChecklistMatrixName: record.adasChecklistMatrixName,
    vehicleMake: make.value,
    make: make.name,
    system: record.system,
    location: record.location,
    yearStart: record.yearStart,
    yearEnd: record.yearEnd
  }
}

export async function findLaunchPadVehicleLinks(model, services) {
  const result = { records: [], group: null, message: '' }
  const isCurrent = services.isCurrent || (() => true)
  const make = await resolveMake(model, services)
  if (!isCurrent()) {
    return result
  }
  if (!make) {
    result.message = `'${(model && model.vehicleMakeName) || ''}' is not a make in the LaunchPad matrix.`
    return result
  }
  // as the ADAS Matrix tab's "by Vehicle" listing: the make's matrix records for this model
  const matrices = (await services.getAdasMatrices({ type: 'byVehicle', id: make.value, filter: reportedModel(model) })) || []
  if (!isCurrent()) {
    return result
  }
  const records = matrices.filter((record) => matchesMatrixRecord(model, record))
  // and as its record dialog: each record's links, by the record they belong to
  const details = await Promise.all(records.map((record) => services.getAdasMakeMatrix(record.adasChecklistMatrixVehicle)))
  if (!isCurrent()) {
    return result
  }
  const rows = _.flatMap(records, (record, index) => toLinkRows(record, details[index] && details[index].links, make.name))
  // a make-wide record holds every model's links, so the link's own model still decides
  const matches = rows.filter((row) => modelMatches(model, row.model) && yearInRange(model, row))
  if (!matches.length) {
    // the matrix record is there, it just holds no links for this vehicle yet
    result.group = toGroupRecord(model, records, make)
    result.message = 'The LaunchPad matrix holds no link records for this vehicle.'
    return result
  }
  result.records = _.sortBy(matches, ['year', 'model', 'category', 'lookupType'])
  return result
}
